import { GalleryVerticalEnd, LayoutGrid } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router'
import { TENSES, type Tense } from '../../../shared/momentStyle'
import { FilterButton, type Filter } from '@/components/Filters'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { Loader } from '@/components/Loader'
import { SlidingTabs, TabsEnd, type SwipeTab } from '@/components/modern/SwipeTabs'
import { BarRow } from '@/components/nav/BarRow'
import { useTabSwipe } from '@/components/modern/useTabSwipe'
import { Button } from '@/components/ui/button'
import type { Family } from '@/lib/families'
import { useFrame } from '@/lib/frame'
import { useMomentShelves, useMoments, useNowMoments, type MomentFilters } from '@/lib/moments'
import { useIsPhone } from '@/lib/platform'
import { useStuck } from '@/lib/stuck'
import { remember, remembered } from '@/lib/remember'
import { useTree } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { MomentFeed } from './MomentFeed'
import { MomentShelves } from './MomentShelves'
import { MomentWall } from './MomentWall'
import { NowRings } from './NowRings'

type Rows = 'circles' | 'people'
type Tab = 'all' | Rows
const ROWS: Rows[] = ['circles', 'people']
// All, then rows by circle and by person. On a circle's own pages, the circle rows are its spaces, and only when you're
// in more than one.
const tabsFor = (family: Family): SwipeTab<Tab>[] => [
  { key: 'all', label: 'All' },
  ...(family.everyone || family.spaces.length > 1
    ? [{ key: 'circles', label: family.everyone ? 'Circles' : 'Spaces' } as SwipeTab<Tab>]
    : []),
  { key: 'people', label: 'People' },
]
type View = 'feed' | 'cards'
const VIEWS: View[] = ['feed', 'cards']
// Shared with the Traditional page, whose Feed and Cards are the same idea.
const VIEW_KEY = 'gatheredfrom:moments-view'

// Moments in Modern style: tabs (All, Circles, People) that a sideways swipe moves between, rings for people with Now
// moments you haven't seen, and the moments as a feed or as cards, or on Circles and People as a row of stories for
// each. Filter, at the top right (in the top bar on a phone), holds the rest: whose, Now or Then, how they're shown and
// who was there. What it narrows to is kept in the address, so back and forward and a shared link keep it; Feed or
// Cards is also kept on this device.
export function ModernMoments({
  family,
  spaceId,
  empty,
}: {
  family: Family
  spaceId?: string
  empty: React.ReactNode
}) {
  const [params, setParams] = useSearchParams()
  const mine = params.get('mine') === '1'
  const TABS = tabsFor(family)
  const asked = params.get('tense')
  const tense = TENSES.includes(asked as Tense) ? (asked as Tense) : undefined
  const rowsAsked = params.get('rows') as Rows | null
  const rows = rowsAsked && ROWS.includes(rowsAsked) && TABS.some((t) => t.key === rowsAsked) ? rowsAsked : undefined
  const tab: Tab = rows ?? 'all'
  const filters: MomentFilters = { tense, person: params.get('person') ?? undefined }
  const viewAsked = params.get('view') as View | null
  const view = viewAsked && VIEWS.includes(viewAsked) ? viewAsked : remembered<View>(VIEW_KEY, VIEWS, 'feed')
  const set = (change: Partial<Record<'tense' | 'rows' | 'person' | 'mine' | 'view', string | undefined>>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(change)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setParams(next, { replace: true })
  }
  const show = (v: View) => {
    remember(VIEW_KEY, v)
    set({ view: v === 'feed' ? undefined : v })
  }
  const turn = (key: Tab) => set({ rows: key === 'all' ? undefined : key })

  const now = useNowMoments(family.id, spaceId)
  const moments = useMoments(family.id, mine, spaceId, filters, !rows)
  const shelves = useMomentShelves(family.id, rows ?? 'people', mine, spaceId, filters, !!rows)
  const tree = useTree(family.id)
  const list = moments.data?.pages.flatMap((p) => p.moments) ?? []
  const narrowed = !!(filters.tense || rows || filters.person || mine)

  const { root, tabRow, pager } = useTabSwipe({
    keys: TABS.map((t) => t.key),
    active: tab,
    still: true,
    onTurn: turn,
  })
  useStuck(tabRow)

  const end = useRef<HTMLDivElement>(null)
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = moments
  useEffect(() => {
    const target = end.current
    if (!target || !hasNextPage) return
    const watch = new IntersectionObserver(
      (seen) => {
        if (seen.some((x) => x.isIntersecting) && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '1200px 0px' },
    )
    watch.observe(target)
    return () => watch.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const phone = useIsPhone()
  const { actionSlot } = useFrame()

  if (!rows && moments.data && list.length === 0 && !narrowed && !now.data?.people.length)
    return <div className={page}>{empty}</div>

  const filterList: Filter[] = [
    {
      heading: 'Whose',
      label: 'Whose',
      value: mine ? 'mine' : 'everyone',
      usual: 'everyone',
      options: [
        ['everyone', 'Everyone’s'],
        ['mine', 'Only mine'],
      ],
      onChange: (w) => set({ mine: w === 'mine' ? '1' : undefined }),
    },
    {
      heading: 'Then or now',
      label: 'Then or now',
      value: tense ?? 'all',
      usual: 'all',
      options: [
        ['all', 'All'],
        ['now', 'Now'],
        ['then', 'Then'],
      ],
      onChange: (t) => set({ tense: t === 'all' ? undefined : t }),
    },
    ...(rows
      ? []
      : ([
          {
            heading: 'Show as',
            label: 'Show moments as',
            value: view,
            options: [
              ['feed', 'Feed'],
              ['cards', 'Cards'],
            ],
            icons: { feed: GalleryVerticalEnd, cards: LayoutGrid },
            onChange: (v: string) => show(v as View),
          },
        ] satisfies Filter[])),
    ...(tree.data && tree.data.people.some((p) => !p.pet)
      ? [
          {
            heading: 'Who was there',
            label: 'Who was there',
            value: filters.person ?? 'anyone',
            usual: 'anyone',
            options: [
              ['anyone', 'Anyone'] as [string, string],
              ...tree.data.people
                .filter((p) => !p.pet)
                .map((p): [string, string] => [p.id, p.name])
                .sort((a, b) => a[1].localeCompare(b[1])),
            ],
            onChange: (p: string) => set({ person: p === 'anyone' ? undefined : p }),
          },
        ]
      : []),
  ]
  const filter = (
    <FilterButton
      title="Filter"
      className={phone ? 'order-first bg-transparent hover:bg-transparent active:opacity-60' : undefined}
      filters={filterList}
    />
  )

  return (
    <div ref={root} className="flex flex-col gap-4 nav:gap-5">
      <BarRow ref={tabRow} pinned className={cn(page, 'mp-tabs flex items-center gap-3 max-sm:px-0')}>
        <SlidingTabs tabs={TABS} active={tab} onChoose={turn} />
        <TabsEnd>{phone ? actionSlot && createPortal(filter, actionSlot) : filter}</TabsEnd>
      </BarRow>
      {/* Tall enough to swipe anywhere under the tabs, even when a tab holds just one moment. */}
      <div ref={pager} className={cn(page, 'flex min-h-[70svh] flex-col gap-6')}>
        {!rows && tense !== 'then' && now.data && now.data.people.length > 0 && <NowRings people={now.data.people} />}
        {rows ? (
          <>
            <FormError error={shelves.error} />
            {!shelves.data && !shelves.error && <Loader label="Loading moments" />}
            {shelves.data && shelves.data.shelves.length === 0 && (
              <p className="py-10 text-center text-muted-foreground">No moments match these filters.</p>
            )}
            {shelves.data && shelves.data.shelves.length > 0 && (
              <MomentShelves shelves={shelves.data.shelves} label={rows === 'people' ? 'People' : 'Circles'} />
            )}
          </>
        ) : (
          <>
            <FormError error={moments.error} />
            {!moments.data && !moments.error && <Loader label="Loading moments" />}
            {moments.data && list.length === 0 && (
              <p className="py-10 text-center text-muted-foreground">
                {tense === 'now' && !filters.person && !mine
                  ? 'Nothing’s been written as it happened yet.'
                  : 'No moments match these filters.'}
              </p>
            )}
            {list.length > 0 &&
              (view === 'cards' ? <MomentWall moments={list} tight /> : <MomentFeed moments={list} />)}
            <div ref={end} className="flex justify-center">
              {hasNextPage &&
                (isFetchingNextPage ? (
                  <Loader label="Loading more moments" now />
                ) : (
                  <Button variant="outline" onClick={() => void fetchNextPage()}>
                    Show more moments
                  </Button>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
